import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  SaveRounded as SaveIcon,
} from "@mui/icons-material";
import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { isFiniteNumber } from "common/numbers";
import Component from "components/Component";
import Button from "components/common/Button";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import {
  currentPeriod,
  MONTH_NAMES,
  periodKey,
  PLAN_CONFIG_KEY,
  resolveScorecardConfig,
} from "components/reportsV3/utils/scorecardUtils";
import { isEqual } from "lodash";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as reportsV3Service from "services/reportsV3Service";
import {
  useAdminUpdateConfigMutation,
  useConfigQuery,
} from "services/staticDataService";

const REVIEW_TABLE = "review_list";

/**
 * The plan measures a company's scorecard reads: one per `plan` column of
 * `submission_review_config.scorecard`, in config order, each named once.
 */
export const planMeasures = (config) => {
  const seen = new Set();
  return (config?.columns ?? []).flatMap((column) => {
    if (typeof column.plan !== "string" || seen.has(column.plan)) return [];
    seen.add(column.plan);
    return [{ key: column.plan, label: column.label }];
  });
};

/** Immutable set of periods[period][bucket][measure]; null clears the cell. */
export const setPlanValue = (periods, period, bucket, measure, value) => {
  const bucketValues = { ...(periods?.[period]?.[bucket] ?? {}) };
  if (value == null) {
    delete bucketValues[measure];
  } else {
    bucketValues[measure] = value;
  }
  return {
    ...(periods ?? {}),
    [period]: { ...(periods?.[period] ?? {}), [bucket]: bucketValues },
  };
};

/** Drop empty buckets and periods, keep only finite numbers. */
export const cleanPeriods = (periods) => {
  const out = {};
  Object.entries(periods ?? {}).forEach(([period, buckets]) => {
    const kept = {};
    Object.entries(buckets ?? {}).forEach(([bucket, measures]) => {
      const values = Object.fromEntries(
        Object.entries(measures ?? {})
          .filter(([, v]) => isFiniteNumber(v))
          .map(([k, v]) => [k, Number(v)])
      );
      if (Object.keys(values).length) kept[bucket] = values;
    });
    if (Object.keys(kept).length) out[period] = kept;
  });
  return out;
};

/** Sum of a bucket/measure over the twelve months of a year. */
export const yearTotal = (periods, year, bucket, measure) =>
  Array.from({ length: 12 }, (_, i) => periodKey(year, i + 1)).reduce(
    (sum, period) => {
      const v = periods?.[period]?.[bucket]?.[measure];
      return isFiniteNumber(v) ? sum + Number(v) : sum;
    },
    0
  );

const format = (value) =>
  isFiniteNumber(value) ? Number(value).toLocaleString("en-US") : "";

/**
 * Settings > Scorecard Plan: the monthly plan figures the Review tab's
 * scorecard compares against, one row per month of the chosen year and one
 * column per bucket x plan measure of the company's scorecard config. Saved
 * whole to `review_scorecard_plan` through admin-update-config (the endpoint
 * merges top-level keys, so the full `periods` object is always sent; the plan
 * is refetched on mount so the whole-document save starts from a fresh copy).
 */
const ScorecardPlanConfig = () => {
  const { enqueueSnackbar } = useSnackbar();
  const reviewConfigQuery = useConfigQuery("submission_review_config", {
    refetchOnMountOrArgChange: true,
  });
  const columnsQuery = reportsV3Service.useGetColumnsForTablesQuery([
    REVIEW_TABLE,
  ]);
  const planQuery = useConfigQuery(PLAN_CONFIG_KEY, {
    refetchOnMountOrArgChange: true,
  });
  const { data: plan, refetch: refetchPlan } = planQuery;
  const [
    updateConfig,
    { isLoading: isSaving },
  ] = useAdminUpdateConfigMutation();

  // Validate the scorecard against the table's real columns, exactly as the
  // Review tab does, so this page never offers plan cells the panel drops.
  const knownColumnNames = useMemo(
    () =>
      new Set(
        (columnsQuery.data ?? []).map((c) => c.internalName).filter(Boolean)
      ),
    [columnsQuery.data]
  );
  const config = useMemo(
    () =>
      knownColumnNames.size
        ? resolveScorecardConfig(reviewConfigQuery.data, knownColumnNames)
        : null,
    [reviewConfigQuery.data, knownColumnNames]
  );
  const measures = useMemo(() => planMeasures(config), [config]);
  const columns = useMemo(
    () =>
      (config?.buckets ?? []).flatMap((bucket) =>
        measures.map((measure) => ({ bucket, measure }))
      ),
    [config, measures]
  );

  const [year, setYear] = useState(() => currentPeriod().year);
  const [periods, setPeriods] = useState({});
  // The stored plan the edits are measured against. A refetch replaces the
  // edits only while there are none, so typing during a refetch is kept.
  // Compared through refs rather than a functional setState: React may run an
  // updater lazily, after the baseline ref has already moved on.
  const baselineRef = useRef({});
  const periodsRef = useRef(periods);
  periodsRef.current = periods;
  useEffect(() => {
    if (plan == null) return;
    const stored = plan.periods ?? {};
    const untouched = isEqual(
      cleanPeriods(periodsRef.current),
      cleanPeriods(baselineRef.current)
    );
    baselineRef.current = stored;
    if (untouched) setPeriods(stored);
  }, [plan]);

  const dirty = !isEqual(
    cleanPeriods(periods),
    cleanPeriods(baselineRef.current)
  );

  // Cells whose text does not parse as a number; Save waits for them.
  const [invalidCells, setInvalidCells] = useState({});
  const hasInvalid = Object.values(invalidCells).some(Boolean);

  const cellId = (period, column) =>
    `${period}|${column.bucket.key}|${column.measure.key}`;

  const setCell = useCallback(
    (period, column, value) =>
      setPeriods((p) =>
        setPlanValue(p, period, column.bucket.key, column.measure.key, value)
      ),
    []
  );
  const setValid = useCallback(
    (id, ok) =>
      setInvalidCells((cells) =>
        cells[id] === !ok ? cells : { ...cells, [id]: !ok }
      ),
    []
  );

  const save = async () => {
    const resp = await updateConfig({
      key: PLAN_CONFIG_KEY,
      value: { periods: cleanPeriods(periods) },
    });
    if (resp?.error || !resp?.data) {
      enqueueSnackbar("Failed to save the scorecard plan", {
        variant: "error",
      });
      return;
    }
    const saved = resp.data.periods ?? {};
    baselineRef.current = saved;
    setPeriods(saved);
    enqueueSnackbar("Scorecard plan saved", { variant: "success" });
    // Read it back the way the scorecard will. A null here means the key is
    // not registered on this environment: the write landed, but nothing can
    // read it until static-data is deployed.
    const readBack = await refetchPlan();
    if (readBack?.data == null) {
      enqueueSnackbar(
        "Saved, but the plan could not be read back. The review_scorecard_plan key may not be deployed on this environment yet.",
        { variant: "warning" }
      );
    }
  };

  const title = "Scorecard Plan";
  const isLoading =
    reviewConfigQuery.isLoading ||
    columnsQuery.isLoading ||
    planQuery.isLoading;
  const isFetching =
    reviewConfigQuery.isFetching ||
    columnsQuery.isFetching ||
    planQuery.isFetching;
  const isError =
    reviewConfigQuery.isError || columnsQuery.isError || planQuery.isError;

  if (isError) {
    return (
      <Component title={title}>
        <Typography variant={"body2"} color={"error"}>
          {"Could not load the scorecard plan. Please try again."}
        </Typography>
      </Component>
    );
  }
  if (!isLoading && !config) {
    return (
      <Component title={title}>
        <Typography variant={"body2"}>
          {
            "No scorecard is configured for this company, so there is no plan to edit. Contact support@cactus.bm to set one up."
          }
        </Typography>
      </Component>
    );
  }
  if (config && columns.length === 0) {
    return (
      <Component title={title}>
        <Typography variant={"body2"}>
          {"This company's scorecard has no plan columns."}
        </Typography>
      </Component>
    );
  }

  return (
    <Component
      title={title}
      loading={isLoading}
      isFetching={isFetching}
      options={
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <IconButton
            size={"small"}
            onClick={() => setYear((y) => y - 1)}
            aria-label={"previous year"}
          >
            <ChevronLeftIcon fontSize={"small"} />
          </IconButton>
          <Typography
            variant={"subtitle1"}
            sx={{ minWidth: 56, textAlign: "center" }}
            data-testid={"scorecard-plan-year"}
          >
            {year}
          </Typography>
          <IconButton
            size={"small"}
            onClick={() => setYear((y) => y + 1)}
            aria-label={"next year"}
          >
            <ChevronRightIcon fontSize={"small"} />
          </IconButton>
          <Button
            size={"small"}
            startIcon={<SaveIcon />}
            onClick={save}
            isLoading={isSaving}
            isDisabled={!dirty || hasInvalid}
            tooltip={hasInvalid ? "Fix the highlighted cells first" : null}
            tooltipRequired={hasInvalid}
            data-testid={"scorecard-plan-save"}
          >
            {"Save"}
          </Button>
        </Stack>
      }
    >
      <Typography variant={"body2"} sx={{ mb: 2 }}>
        {
          "Monthly plan figures for the Review tab scorecard. Enter a value per month and bucket; blank means no plan. Save writes the whole plan for every year."
        }
      </Typography>
      <Box sx={{ overflowX: "auto" }}>
        <Table size={"small"} aria-label={title}>
          <TableHead>
            <TableRow>
              <TableCell>{"Month"}</TableCell>
              {columns.map((column) => (
                <TableCell
                  key={cellId("head", column)}
                  align={"right"}
                  sx={{ minWidth: 160 }}
                >
                  {measures.length > 1
                    ? `${column.bucket.label} — ${column.measure.label}`
                    : column.bucket.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {MONTH_NAMES.map((name, index) => {
              const period = periodKey(year, index + 1);
              return (
                <TableRow key={period}>
                  <TableCell component={"th"} scope={"row"}>
                    {name}
                  </TableCell>
                  {columns.map((column) => {
                    const id = cellId(period, column);
                    const stored =
                      periods?.[period]?.[column.bucket.key]?.[
                        column.measure.key
                      ];
                    return (
                      <TableCell key={id} align={"right"}>
                        <PrettyNumberTextField
                          size={"small"}
                          variant={"outlined"}
                          value={isFiniteNumber(stored) ? Number(stored) : null}
                          onChangeNumber={(n) => setCell(period, column, n)}
                          onValidation={(ok) => setValid(id, ok)}
                          offFormatter={format}
                          inputProps={{
                            inputMode: "decimal",
                            style: { textAlign: "right" },
                            "aria-label": `${name} ${column.bucket.label} ${column.measure.label}`,
                          }}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
            <TableRow sx={{ "& td, & th": { fontWeight: 700 } }}>
              <TableCell component={"th"} scope={"row"}>
                {`Total ${year}`}
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={cellId("total", column)}
                  align={"right"}
                  data-testid={`scorecard-plan-total-${column.bucket.key}-${column.measure.key}`}
                >
                  {format(
                    yearTotal(
                      periods,
                      year,
                      column.bucket.key,
                      column.measure.key
                    )
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Component>
  );
};

export default ScorecardPlanConfig;
